
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { motion } from "framer-motion";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import InquiryForm from "@/components/services/InquiryForm";

interface SolutionItem {
  id: number | string;
  title: string;
  description: string;
  icon: React.ElementType;
  technologies: string[];
  slug: string;
}

interface SolutionCardProps {
  solution: SolutionItem;
  index: number;
}

const SolutionCard = ({ solution, index }: SolutionCardProps) => {
  return (
    <motion.div
      key={solution.id}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      className="group"
    >
      <Card className="h-full curved-xl transform transition-all duration-300 hover:scale-105 shadow-soft card-gradient border border-gray-200 dark:border-gray-700">
        <CardHeader>
          <div className="flex items-center gap-4">
            <div className="p-3 bg-indigo-100 dark:bg-indigo-900 rounded-xl">
              {React.createElement(solution.icon, { className: "h-8 w-8 text-indigo-600 dark:text-indigo-400" })}
            </div>
            <CardTitle className="text-xl font-bold text-gray-800 dark:text-gray-200">
              {solution.title}
            </CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          <p className="text-gray-600 dark:text-gray-300 mb-4">
            {solution.description}
          </p>
          <div className="mb-4">
            <h4 className="font-medium mb-2 text-gray-700 dark:text-gray-300">
              Features & Technologies:
            </h4>
            <div className="flex flex-wrap gap-2">
              {solution.technologies.map((tech) => (
                <span
                  key={tech}
                  className="bg-indigo-100 dark:bg-indigo-900 text-indigo-800 dark:text-indigo-200 text-sm px-3 py-1 rounded-full"
                >
                  {tech}
                </span>
              ))}
            </div>
          </div>
          <div className="flex flex-col space-y-2">
            <Link to={`/solutions/${solution.slug}`}>
              <Button 
                variant="outline"
                className="w-full curved-xl mb-2 group"
              >
                Learn More
                <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Button>
            </Link>
            
            <Sheet>
              <SheetTrigger asChild>
                <Button 
                  className="w-full curved-xl btn-gradient text-white transform hover:scale-105 transition-all duration-300"
                >
                  Get Started
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[90vw] sm:w-[540px] overflow-y-auto curved-xl">
                <SheetHeader>
                  <SheetTitle>Inquire about {solution.title}</SheetTitle>
                  <SheetDescription>
                    Please fill out the form below and we'll get back to you shortly.
                  </SheetDescription>
                </SheetHeader>
                <InquiryForm service={solution.title} formType="solution" />
              </SheetContent>
            </Sheet>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
};

export default SolutionCard;
